import React, { useState } from 'react';
import { StyleSheet } from 'react-native';
import { Link } from 'react-router-dom';

import { canUser } from '../support/shared/permissionHelpers';

import LoadingIndicator from './uiold/LoadingIndicator';
import { Button, ButtonToolbar, Card, Page, Text } from './uiold';

import MapMarketItem from './MapEditor/MapMarketItem';
import MapMarkerEditor from './MapEditor/MapMarkerEditor';

import { useAuthState } from './AuthState';
import { PositionMapByMarket } from '../types/shared/PositionMap';
import { Market } from '../types/shared/Market';
import { useShowMessage } from '../core-ui';
import { useFetchJson } from '../support/useFetch';
import { useMutation, useQuery } from '@tanstack/react-query';
import IndexedList from '../support/shared/IndexedList';
import DataStore from '../support/DataStore';

const dataStore = DataStore.getInstance('app');

type Props = {
  markets: Array<Market>;
  position: PositionMapByMarket;
  onSaveMarkets: (markets: Array<Market>) => void;
  isSaving: boolean;
};

function MapEditorView(props: Props) {
  const { position, onSaveMarkets, isSaving } = props;
  const [editingMarketId, setEditingMarketId] = useState<string | null>(null);
  const { user } = useAuthState();
  const [markets, setMarkets] = useState(props.markets);

  const setMarket = (newMarket: Market) => {
    setMarkets((markets) =>
      markets.map((market) =>
        market.id === newMarket.id ? newMarket : market,
      ),
    );
  };

  const renderButtonToolbar = () => {
    if (isSaving) {
      return <LoadingIndicator />;
    }
    return (
      <ButtonToolbar>
        <Button
          title={t('Revert All')}
          onPress={() => {
            setEditingMarketId(null);
            setMarkets(props.markets);
          }}
        />
        <Button
          title={t('Save Changes')}
          onPress={() => {
            onSaveMarkets(markets);
          }}
        />
      </ButtonToolbar>
    );
  };

  const onEditEnd = () => {
    setEditingMarketId(null);
  };

  const market = editingMarketId
    ? markets.find(({ id }) => id === editingMarketId)
    : undefined;
  let pageTitle = market ? t('Edit Market Map') : t('Maps');
  return (
    <Page>
      <Page.Header title={pageTitle}>{renderButtonToolbar()}</Page.Header>
      {markets.length === 0 && (
        <Text>
          {/* Should we <Redirect /> here instead of providing a link? */}
          {t.jsx(
            'You have no markets. <link>Click here</link> to add a market.',
            { link: (content) => <Link to="/markets">{content}</Link> },
          )}
        </Text>
      )}
      {market ? (
        <Card style={styles.editor}>
          <Card.Header title={market.name}>
            <Button onPress={onEditEnd} title={t('Done')} isPrimary />
          </Card.Header>
          <Text style={styles.editorText}>
            {t(
              'Click on any location on the map to add a Marker, drag to move, and click on the marker to delete it.',
            )}
          </Text>
          <div className="marker-editor">
            <MapMarkerEditor
              market={market}
              position={position}
              onAppendMarker={(position) => {
                const { map } = market;
                if (map) {
                  const newMarkers = map.markers.slice();
                  const firstNull = newMarkers.indexOf(null);
                  if (firstNull >= 0) {
                    newMarkers[firstNull] = position;
                  } else {
                    newMarkers.push(position);
                  }
                  setMarket({
                    ...market,
                    map: { ...map, markers: newMarkers },
                  });
                }
              }}
              onUpdateMarker={(index, position) => {
                const { map } = market;
                if (map && index < map.markers.length) {
                  const newMarkers = map.markers.slice();
                  newMarkers[index] = position;
                  setMarket({
                    ...market,
                    map: { ...map, markers: newMarkers },
                  });
                }
              }}
            />
          </div>
        </Card>
      ) : (
        markets.map((market) => {
          return (
            <MapMarketItem
              key={market.id}
              market={market}
              onBeginEdit={() => setEditingMarketId(market.id)}
              canEdit={canUser(user, { vendorPlacement: 'WRITE' })}
              onImageChange={(imageId, imageType) => {
                const existingMarkers = market.map?.markers;
                setMarket({
                  ...market,
                  map: {
                    id: imageId,
                    imageType,
                    markers: existingMarkers || [],
                  },
                });
              }}
              onImageRemove={() => {
                setMarket({ ...market, map: null });
              }}
            />
          );
        })
      )}
    </Page>
  );
}

let styles = StyleSheet.create({
  editor: {
    marginBottom: 30,
  },
  editorText: {
    marginTop: 10,
    marginBottom: 20,
    paddingLeft: 20,
  },
});

export default function MapEditor() {
  const showMessage = useShowMessage();
  const fetchJson = useFetchJson();

  const { data: markets, error: error1, refetch } = useQuery(
    ['markets'],
    async () => {
      const url = '/api/markets';
      const data = await fetchJson<{ markets: Array<Market> }>(url);
      return data.markets;
    },
    {
      onSuccess: (markets) => {
        // For legacy reasons, update the data store after successful fetch
        dataStore.set('markets', new IndexedList(markets));
      },
    },
  );

  const { data: position, error: error2 } = useQuery(
    ['/api/position'],
    async () => {
      const url = '/api/position';
      const data = await fetchJson<{ positionMap: PositionMapByMarket }>(url);
      return data.positionMap;
    },
    {
      onSuccess: (positionMap) => {
        // For legacy reasons, update the data store after successful fetch
        dataStore.set('position', positionMap);
      },
    },
  );

  const { mutate: saveAll } = useMutation(
    ['POST:/api/markets/:id'],
    async (markets: Array<Market>) => {
      for (const market of markets) {
        const { id, map } = market;
        await fetchJson(`/api/markets/${id}`, { body: { updates: { map } } });
      }
    },
    {
      onSuccess: () => {
        showMessage('success', t('Saved successfully'));
        void refetch();
      },
      onError: (error) => {
        showMessage('error', t('Error saving changes: {error}', { error }));
      },
    },
  );

  if (error1 || error2) {
    return <p>{String(error1 || error2)}</p>;
  }

  if (!markets || !position) {
    return <LoadingIndicator />;
  }

  return (
    <MapEditorView
      markets={markets}
      position={position}
      onSaveMarkets={(markets) => saveAll(markets)}
      isSaving={false}
    />
  );
}
